import React, { useState, useEffect } from 'react';
import { Mail, MailOpen, CheckCircle, Calendar, User } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const MessagesManagement = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all', 'unread', 'read'

  useEffect(() => {
    fetchMessages();
  }, [filter]);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const unreadOnly = filter === 'unread';
      const response = await axios.get(`${API}/api/contact-messages?unread_only=${unreadOnly}`);
      const fetchedMessages = response.data;
      
      if (filter === 'read') {
        setMessages(fetchedMessages.filter(msg => msg.is_read));
      } else {
        setMessages(fetchedMessages);
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (messageId) => {
    try {
      await axios.patch(`${API}/api/contact-messages/${messageId}/read`);
      toast.success('Marked as read');
      fetchMessages();
    } catch (error) {
      console.error('Error marking message as read:', error);
      toast.error('Failed to update message');
    }
  };

  const markAsReplied = async (messageId) => {
    try {
      await axios.patch(`${API}/api/contact-messages/${messageId}/replied`);
      toast.success('Marked as replied');
      fetchMessages();
    } catch (error) {
      console.error('Error marking message as replied:', error);
      toast.error('Failed to update message');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const unreadCount = messages.filter(msg => !msg.is_read).length;

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Contact Messages</h1>
        <p className="text-gray-600">View and manage customer inquiries</p>
      </div>

      {/* Filter Tabs */}
      <div className="mb-6 flex space-x-2 border-b border-gray-200">
        <button
          onClick={() => setFilter('all')}
          className={`px-4 py-2 font-medium ${
            filter === 'all'
              ? 'text-brand-purple border-b-2 border-brand-purple'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          All Messages ({messages.length})
        </button>
        <button
          onClick={() => setFilter('unread')}
          className={`px-4 py-2 font-medium ${
            filter === 'unread'
              ? 'text-brand-purple border-b-2 border-brand-purple'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Unread ({unreadCount})
        </button>
        <button
          onClick={() => setFilter('read')}
          className={`px-4 py-2 font-medium ${
            filter === 'read'
              ? 'text-brand-purple border-b-2 border-brand-purple'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Read
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-purple mx-auto"></div>
          <p className="text-gray-600 mt-4">Loading messages...</p>
        </div>
      ) : messages.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <Mail className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No messages yet</h3>
          <p className="text-gray-600">Customer messages will appear here</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Messages List */}
          <div className="space-y-4">
            {messages.map((message) => (
              <div
                key={message.id}
                onClick={() => {
                  setSelectedMessage(message);
                  if (!message.is_read) {
                    markAsRead(message.id);
                  }
                }}
                className={`bg-white p-4 rounded-lg border cursor-pointer hover:shadow-md transition-shadow ${
                  !message.is_read
                    ? 'border-brand-purple bg-purple-50'
                    : 'border-gray-200'
                } ${
                  selectedMessage?.id === message.id
                    ? 'ring-2 ring-brand-purple'
                    : ''
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    {message.is_read ? (
                      <MailOpen className="w-5 h-5 text-gray-400" />
                    ) : (
                      <Mail className="w-5 h-5 text-brand-purple" />
                    )}
                    <span className="font-semibold text-gray-900">{message.name}</span>
                  </div>
                  {message.replied && (
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full flex items-center">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Replied
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-600 mb-2 truncate">{message.email}</p>
                <p className="text-sm font-medium text-gray-900 mb-2">{message.subject}</p>
                <p className="text-sm text-gray-600 truncate mb-2">{message.message}</p>
                <div className="flex items-center text-xs text-gray-500">
                  <Calendar className="w-3 h-3 mr-1" />
                  {formatDate(message.created_at)}
                </div>
              </div>
            ))}
          </div>

          {/* Message Detail */}
          <div className="bg-white p-6 rounded-lg border border-gray-200 sticky top-6 h-fit">
            {selectedMessage ? (
              <div>
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">
                      {selectedMessage.subject}
                    </h2>
                    <div className="flex items-center text-gray-600 mb-2">
                      <User className="w-4 h-4 mr-2" />
                      {selectedMessage.name}
                    </div>
                    <a
                      href={`mailto:${selectedMessage.email}`}
                      className="text-brand-purple hover:underline text-sm"
                    >
                      {selectedMessage.email}
                    </a>
                  </div>
                  <div className="text-sm text-gray-500">
                    {formatDate(selectedMessage.created_at)}
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg mb-6">
                  <p className="text-gray-700 whitespace-pre-wrap">
                    {selectedMessage.message}
                  </p>
                </div>

                <div className="flex space-x-3">
                  <Button
                    asChild
                    className="bg-brand-purple hover:bg-brand-purple-dark text-white flex-1"
                  >
                    <a href={`mailto:${selectedMessage.email}?subject=Re: ${selectedMessage.subject}`}>
                      Reply via Email
                    </a>
                  </Button>
                  {!selectedMessage.replied && (
                    <Button
                      onClick={() => markAsReplied(selectedMessage.id)}
                      variant="outline"
                      className="flex-1"
                    >
                      Mark as Replied
                    </Button>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <Mail className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-600">Select a message to view details</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MessagesManagement;
